import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { MedicationListItem } from '../../../../../store/hubOwner/types';
import {
  dimGrey,
  inactive,
  lightBackground1,
  lightBlack,
} from '../../../../../mainTheme';

interface UsageDetailsProps {
  rowData: MedicationListItem | null;
}

const DetailLabel = styled(Typography)({
  color: lightBlack,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.2px',
  marginBottom: '4px',
});

const DetailValue = styled(Typography)({
  color: dimGrey,
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.2px',
});

const UsageDetailBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0px 16px',
  borderRadius: '4px',
  border: `2px solid ${inactive}`,
  backgroundColor: lightBackground1,
  minHeight: '48px',
  minWidth: '72px',
  flex: 1,
});

const UsageCount = styled(Typography)({
  color: lightBlack,
  fontWeight: 500,
  fontSize: '22px',
  lineHeight: '30px',
});

const UsageLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  color: dimGrey,
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: '8px',
});

const UsageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const MedicationUsageDetails: React.FC<UsageDetailsProps> = ({ rowData }) => {
  // Standard UI labels to show
  const DESIGN_LABELS = [
    'SOBER',
    'MORNING',
    'NOON',
    'EVENING',
    'BEDTIME',
    'PRN',
  ];

  // Normalize for matching
  const normalize = (s: string) => s.trim().toUpperCase();
  // Simplified: if API has 6+ tags, show first 6 API tags; else fill with DESIGN_LABELS in order
  const responseTags = (rowData?.time_tags || []).map(normalize);
  let displayLabels: { label: string; value: 1 | '' }[] = [];
  if (responseTags.length >= 6) {
    displayLabels = responseTags
      .slice(0, 6)
      .map((label) => ({ label, value: 1 }));
  } else {
    // Fill with DESIGN_LABELS, using API tags where present, else show label with empty value
    const used = new Set<string>();
    for (let i = 0; i < DESIGN_LABELS.length && displayLabels.length < 6; i++) {
      const label = DESIGN_LABELS[i];
      if (responseTags.includes(label)) {
        displayLabels.push({ label, value: 1 });
        used.add(label);
      } else {
        // If any API tag is not a standard label and not used, insert it in the next available slot
        const extra = responseTags.find(
          (tag) => !DESIGN_LABELS.includes(tag) && !used.has(tag),
        );
        if (extra) {
          displayLabels.push({ label: extra, value: 1 });
          used.add(extra);
        } else {
          displayLabels.push({ label, value: '' });
        }
      }
    }
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }} data-testid="medication-usage-details">
        <DetailLabel>Usage details</DetailLabel>
        <DetailValue>
          {/* TODO: Here the below details hidden for now, as per the design spec this needs to be from API ,but we don't have this data now */}
          {/* EXAMPLE - Take (1) tablet by mouth. 2X Daily. 1 morning and 1 bedtime. */}
        </DetailValue>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {displayLabels.map(({ label, value }) => (
          <UsageContainer key={label}>
            <UsageLabel>{label}</UsageLabel>
            <UsageDetailBox>
              <UsageCount>{value}</UsageCount>
            </UsageDetailBox>
          </UsageContainer>
        ))}
      </Box>
    </Box>
  );
};

export default MedicationUsageDetails;
